import { create } from 'zustand';

import * as sessionApi from './session.api';
import { type SessionState, type SessionUser } from './session.types';

/**
 * Sesion del usuario.
 *
 * Vive en `shared/` y no en la feature de autenticacion porque la necesita
 * todo el sistema: cada pantalla pregunta quien es el usuario y que permisos
 * tiene. Dejarla dentro de una feature obligaria a las demas a importar sus
 * internals, que es justo lo que la arquitectura por features prohibe -y lo
 * que el linter verifica-.
 *
 * El access token vive SOLO en memoria. No se guarda en `localStorage` ni en
 * `sessionStorage` a proposito: lo que hay ahi lo lee cualquier script que
 * llegue a ejecutarse en el dominio, y una credencial persistida sobrevive al
 * cierre de la pestana. Que se pierda al recargar no es un problema, porque la
 * cookie `httpOnly` permite recuperar la sesion sin volver a pedir la
 * contrasena: eso es justamente lo que hace `restore()`.
 */

interface SessionActions {
  /** Recupera la sesion al arrancar, si la cookie sigue siendo valida. */
  restore: () => Promise<void>;
  signIn: (credentials: sessionApi.Credentials) => Promise<void>;
  signOut: (allDevices?: boolean) => Promise<void>;
  /** Renueva el access token. Devuelve `false` si ya no hay sesion. */
  renew: () => Promise<boolean>;
  /** Refresca el perfil tras cambiar la contrasena o los permisos. */
  reload: () => Promise<void>;
}

const ANONIMA: SessionState = { status: 'anonima', accessToken: null, user: null };

/**
 * Recuperacion en curso.
 *
 * El backend ROTA el refresh token en cada uso y trata la reutilizacion de uno
 * ya rotado como robo, revocando la sesion entera. Eso convierte dos llamadas
 * simultaneas en una expulsion: la segunda llega con el token que la primera
 * acaba de invalidar.
 *
 * Ocurre con facilidad: React en modo estricto monta los efectos dos veces
 * durante el desarrollo, y dos componentes pueden pedir la sesion a la vez.
 * Compartiendo la promesa, todos esperan la MISMA renovacion.
 */
let recuperacionEnCurso: Promise<void> | null = null;

export const useSessionStore = create<SessionState & SessionActions>((set, get) => ({
  status: 'desconocida',
  accessToken: null,
  user: null,

  restore: () => {
    recuperacionEnCurso ??= (async () => {
      try {
        const { accessToken } = await sessionApi.refresh();
        set({ accessToken });
        set({ status: 'activa', user: await sessionApi.fetchCurrentUser() });
      } catch {
        // No hay cookie, caduco o fue revocada. Es el caso normal de un
        // navegador que entra por primera vez: no es un error que reportar.
        set(ANONIMA);
      } finally {
        recuperacionEnCurso = null;
      }
    })();

    return recuperacionEnCurso;
  },

  signIn: async (credentials) => {
    // Una sola peticion: la respuesta del login ya trae el perfil entero,
    // `mustChangePassword` incluido. Se aplica de una vez -token, estado y
    // usuario en el mismo `set`- para que ningun render intermedio vea una
    // sesion a medias.
    const { accessToken, user } = await sessionApi.login(credentials);
    set({ accessToken, status: 'activa', user });
  },

  signOut: async (allDevices = false) => {
    try {
      await sessionApi.logout(allDevices);
    } finally {
      // El estado local se limpia pase lo que pase: si el servidor no
      // responde, dejar la sesion pintada haria creer al usuario que sigue
      // dentro cuando ya pidio salir.
      set(ANONIMA);
    }
  },

  renew: async () => {
    try {
      const { accessToken } = await sessionApi.refresh();
      set({ accessToken });
      return true;
    } catch {
      set(ANONIMA);
      return false;
    }
  },

  reload: async () => {
    if (get().status !== 'activa') return;
    set({ user: await sessionApi.fetchCurrentUser() });
  },
}));

/** Acceso puntual fuera de React, para el cliente HTTP. */
export function currentAccessToken(): string | null {
  return useSessionStore.getState().accessToken;
}

export function clearSession(): void {
  useSessionStore.setState(ANONIMA);
}

export function renewSession(): Promise<boolean> {
  return useSessionStore.getState().renew();
}

export function currentUser(): SessionUser | null {
  return useSessionStore.getState().user;
}
